import { Link, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import DetailActions from "@/components/feature/DetailActions";
import EmptyState from "@/components/feature/EmptyState";
import { shortVideos } from "@/mocks/shortVideos";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatDuration, formatViews } from "@/utils/format";

export default function ShortVideoDetail() {
  const { id } = useParams<{ id: string }>();
  const video = shortVideos.find((item) => item.id === id);

  usePageMeta(
    video ? `${video.title} - 午夜视频精选短视频` : "短视频不存在 - 午夜视频",
    video
      ? `${video.title}（${video.tag} · ${formatDuration(video.duration)}），${video.intro.slice(0, 60)}`
      : "没有找到这条短视频，返回短视频分类继续浏览更多精选内容。",
    video ? `${video.title},${video.tag}短视频,午夜短视频,午夜视频,深夜短片` : "午夜视频,午夜短视频",
  );

  if (!video) {
    return (
      <PageLayout>
        <div className="w-full px-4 py-10 md:px-8">
          <Breadcrumb
            items={[{ label: "短视频分类", to: "/short-videos" }, { label: "内容不存在" }]}
          />
          <EmptyState
            icon="ri-video-off-line"
            title="这条短视频已经不在了"
            description="可能是链接失效，或者内容已下架。回到短视频分类，那里还有更多深夜片段可以看。"
            actionLabel="返回短视频分类"
            actionTo="/short-videos"
          />
        </div>
      </PageLayout>
    );
  }

  const related = shortVideos
    .filter((item) => item.id !== video.id && item.tag === video.tag)
    .sort((a, b) => b.views - a.views)
    .slice(0, 4);

  return (
    <PageLayout>
      <div className="w-full px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb items={[{ label: "短视频分类", to: "/short-videos" }, { label: video.title }]} />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div>
            <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-background-700 bg-background-800">
              <img
                src={video.cover}
                alt={`${video.title} 视频封面大图`}
                title={`${video.title} - 午夜视频精选短视频`}
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-background-950/10 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-4 pb-4">
                <span className="flex items-center gap-1.5 rounded-md bg-background-950/70 px-2.5 py-1.5 text-[11px] text-foreground-200 backdrop-blur-sm">
                  <i className="ri-time-line text-xs" />
                  {formatDuration(video.duration)}
                </span>
                <span className="flex items-center gap-1.5 rounded-md bg-background-950/70 px-2.5 py-1.5 text-[11px] text-foreground-200 backdrop-blur-sm">
                  <i className="ri-eye-line text-xs" />
                  {formatViews(video.views)} 次浏览
                </span>
              </div>
            </div>

            <h1 className="mt-5 font-heading text-xl font-bold leading-snug tracking-wide text-foreground-50 md:text-3xl">
              {video.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-foreground-400">
              <span className="rounded-md bg-accent-500 px-2 py-1 font-medium text-background-950">
                {video.tag}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-user-3-line text-sm" />
                {video.creator}
              </span>
              <span className="h-3 w-px bg-background-700" />
              <span>上传于 {video.uploadDate}</span>
              <span className="h-3 w-px bg-background-700" />
              <span>{formatDuration(video.duration)}</span>
            </div>

            <div className="mt-5">
              <DetailActions type="short" id={video.id} />
            </div>

            <section className="mt-7">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-50">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                内容简介
              </h2>
              <p className="mt-3 text-sm leading-8 text-foreground-300">{video.intro}</p>
            </section>

            <div className="mt-7 flex items-start gap-2.5 rounded-lg border border-accent-500/25 bg-primary-950/70 px-4 py-3.5">
              <i className="ri-information-line mt-0.5 text-base text-accent-500" />
              <p className="text-xs leading-6 text-foreground-400">
                本站暂未接入视频播放能力，此页面仅展示封面、时长与内容简介。喜欢这条内容可以先收藏，方便之后回到这里查看。
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {[video.tag, "深夜片段", "精选推荐", video.creator].map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-background-700 bg-background-900 px-3 py-1.5 text-[11px] text-secondary-200"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-800 bg-background-900/70 p-4 md:p-5">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-50">
                  <i className="ri-play-circle-line text-base text-accent-500" />
                  相关短视频
                </h2>
                <Link
                  to="/short-videos"
                  className="text-xs text-foreground-500 transition-colors hover:text-accent-400"
                >
                  更多
                </Link>
              </div>

              {related.length === 0 ? (
                <p className="mt-4 text-xs leading-6 text-foreground-500">
                  这个标签下暂时没有其他内容，去短视频分类看看别的类型吧。
                </p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {related.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/detail/short-video/${item.id}`}
                        className="group flex gap-3 rounded-md p-1.5 transition-colors hover:bg-background-800/70"
                      >
                        <div className="relative h-[56px] w-[100px] shrink-0 overflow-hidden rounded-md border border-background-700 bg-background-800">
                          <img
                            src={item.cover}
                            alt={`${item.title} 封面`}
                            title={`${item.title} - 午夜视频相关短视频`}
                            className="h-full w-full object-cover object-top"
                          />
                          <span className="absolute bottom-1 right-1 rounded bg-background-950/80 px-1 font-label text-[10px] text-foreground-100">
                            {formatDuration(item.duration)}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 py-0.5">
                          <h3 className="line-clamp-2-safe text-xs leading-5 text-foreground-100 transition-colors group-hover:text-accent-400">
                            {item.title}
                          </h3>
                          <p className="mt-1 flex items-center gap-1 text-[11px] text-foreground-500">
                            <i className="ri-eye-line text-xs" />
                            {formatViews(item.views)}
                          </p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              <Link
                to="/movies"
                className="mt-5 flex items-center justify-between rounded-md border border-background-700 bg-background-950/60 px-3 py-2.5 text-xs text-foreground-300 transition-colors hover:border-accent-500/60 hover:text-accent-400"
              >
                想看点长的？进入电影分类
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </PageLayout>
  );
}